// ============================================================
//  App.js  —  Top-level orchestrator
//
//  Phases:
//    "start"  → StartScreen (theme / difficulty / settings selection)
//    "game"   → GameScreen  (active hunt)
//    "win"    → WinScreen   (final results + share)
// ============================================================

import { useState, useEffect } from "react";
import StartScreen from "./StartScreen";
import GameScreen  from "./GameScreen";

// ── Score calculation ────────────────────────────────────────

function calculateScore(totalPages, timerSeconds, wrongHits, hintsUsed, funMode = false) {
  if (funMode) return null; // unranked
  const base         = totalPages * 200;       // 800 / 1200 / 1800 max
  const timePenalty  = Math.floor(timerSeconds / 15) * 5;
  const wrongPenalty = wrongHits  * 50;
  const hintPenalty  = hintsUsed  * 100;
  return Math.max(0, base - timePenalty - wrongPenalty - hintPenalty);
}

function getGrade(score, maxScore) {
  const pct = score / maxScore;
  if (pct >= 0.90) return { grade:"S", color:"#d4a94a" };
  if (pct >= 0.75) return { grade:"A", color:"#4ade80" };
  if (pct >= 0.55) return { grade:"B", color:"#60a5fa" };
  if (pct >= 0.35) return { grade:"C", color:"#facc15" };
  return               { grade:"D", color:"#f87171"  };
}

function fmt(s) {
  return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
}

// ── Shared font import ───────────────────────────────────────
const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700;900&family=Crimson+Pro:wght@400;600&family=Space+Mono:wght@400;700&display=swap');`;

// ── Win Screen ───────────────────────────────────────────────

function WinScreen({ result, onPlayAgain }) {
  const {
    seconds, wrongHits, hintsUsed, totalPages,
    correctHits, pagesVisited, difficulty,
    funMode,
  } = result;

  const maxScore = totalPages * 200;
  const score    = calculateScore(totalPages, seconds, wrongHits, hintsUsed, funMode);
  const { grade, color: gradeColor } = score === null
    ? { grade:"—", color:"#6b6880" }
    : getGrade(score, maxScore);

  function handleShare() {
    const dl   = difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : "";
    const dots = "●".repeat(correctHits) + "○".repeat(Math.max(0, totalPages - correctHits));
    const scoreLine = score === null
      ? "Score: Unranked (Fun Mode)"
      : `Score: ${score} / ${maxScore}  [${grade}]`;
    const text = [
      `WikiScav 🌐  ${dl}`,
      `${dots}  ${correctHits}/${totalPages} anomalies found`,
      `⏱  ${fmt(seconds)}   ✗ ${wrongHits}   💡 ${hintsUsed}`,
      scoreLine,
    ].join("\n");
    navigator.clipboard.writeText(text).catch(() => {});
  }

  return (
    <div style={{
      minHeight:"100vh", background:"#0a0a0f",
      display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", fontFamily:"'Space Mono',monospace",
      color:"#e8e4d8", textAlign:"center", gap:"1.4rem", padding:"2rem 1rem",
    }}>
      <style>{FONT_IMPORT}</style>

      {/* Grade circle */}
      <div style={{
        width:80, height:80, borderRadius:"50%",
        border:`2px solid ${gradeColor}`,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontFamily:"'Cinzel',serif", fontSize:"2.4rem", fontWeight:900,
        color:gradeColor, boxShadow:`0 0 48px ${gradeColor}38`,
      }}>
        {grade}
      </div>

      {/* Title */}
      <div>
        <h1 style={{
          fontFamily:"'Cinzel',serif", color:"#d4a94a",
          fontSize:"clamp(1.8rem,5vw,2.8rem)", margin:0, lineHeight:1,
        }}>
          Hunt Complete
        </h1>
        <p style={{
          color:"#6b6880", fontSize:"0.62rem",
          letterSpacing:"0.2em", textTransform:"uppercase", marginTop:"0.4rem",
        }}>
          {difficulty}{funMode ? " · fun mode" : ""} · {totalPages} pages · {correctHits === totalPages ? "flawless hunt" : `${correctHits}/${totalPages} anomalies`}
        </p>
      </div>

      {/* Score */}
      <div>
        <div style={{
          color:"#6b6880", fontSize:"0.55rem",
          letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:"0.25rem",
        }}>
          Final Score
        </div>
        <div style={{
          fontFamily:"'Cinzel',serif",
          fontSize:"clamp(2.8rem,9vw,4.5rem)",
          color:gradeColor, lineHeight:1,
        }}>
          {score === null ? "Unranked" : score}
        </div>
        <div style={{ color:"#6b6880", fontSize:"0.62rem" }}>
          {score === null ? "Fun Mode run" : `/ ${maxScore}`}
        </div>
      </div>

      {/* Page completion dots */}
      <div style={{ display:"flex", gap:"0.55rem", alignItems:"center" }}>
        {Array.from({ length: totalPages }).map((_, i) => (
          <div key={i} style={{
            width:13, height:13, borderRadius:"50%",
            background:  i < correctHits ? "#4ade80" : "#1a1a26",
            border:     `1px solid ${i < correctHits ? "#4ade80" : "rgba(255,255,255,0.1)"}`,
            boxShadow:   i < correctHits ? "0 0 8px rgba(74,222,128,0.4)" : "none",
          }} />
        ))}
      </div>

      {/* Stats grid */}
      <div style={{
        display:"grid", gridTemplateColumns:"repeat(4,1fr)",
        gap:"1px", background:"rgba(255,255,255,0.06)",
        border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
        overflow:"hidden", width:"100%", maxWidth:480,
      }}>
        {[
          ["Pages",   `${correctHits}/${totalPages}`],
          ["Time",    fmt(seconds)],
          ["Wrong",   wrongHits],
          ["Hints",   hintsUsed],
        ].map(([label, val]) => (
          <div key={label} style={{ background:"#12121a", padding:"1rem 0.5rem" }}>
            <div style={{
              fontSize:"0.5rem", letterSpacing:"0.15em",
              textTransform:"uppercase", color:"#6b6880", marginBottom:"0.3rem",
            }}>
              {label}
            </div>
            <div style={{ fontFamily:"'Cinzel',serif", fontSize:"1.35rem", color:"#d4a94a" }}>
              {val}
            </div>
          </div>
        ))}
      </div>

      {/* Articles visited */}
      {pagesVisited && pagesVisited.length > 0 && (
        <div style={{ maxWidth:480, width:"100%" }}>
          <div style={{
            fontSize:"0.52rem", letterSpacing:"0.18em",
            textTransform:"uppercase", color:"#6b6880", marginBottom:"0.55rem",
          }}>
            Articles Visited
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"0.35rem", justifyContent:"center" }}>
            {pagesVisited.map((p, i) => {
              const done = p.status === "correct";
              return (
                <span key={i} style={{
                  fontFamily:"'Space Mono',monospace", fontSize:"0.62rem",
                  padding:"0.2rem 0.55rem", borderRadius:4,
                  background: done ? "rgba(74,222,128,0.1)" : "#12121a",
                  border:     `1px solid ${done ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.06)"}`,
                  color:      done ? "#4ade80" : "#6b6880",
                }}>
                  {p.title}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display:"flex", gap:"0.75rem", flexWrap:"wrap", justifyContent:"center" }}>
        <button
          onClick={handleShare}
          style={{
            background:"#12121a", color:"#e8e4d8",
            border:"1px solid rgba(255,255,255,0.1)", borderRadius:6,
            padding:"0.75rem 1.5rem", fontFamily:"'Cinzel',serif",
            fontWeight:700, fontSize:"0.78rem", letterSpacing:"0.08em",
            cursor:"pointer", transition:"all 0.15s",
          }}
        >
          📋 Share Result
        </button>
        <button
          onClick={onPlayAgain}
          style={{
            background:"#d4a94a", color:"#0a0a0f", border:"none", borderRadius:6,
            padding:"0.85rem 2.5rem", fontFamily:"'Cinzel',serif",
            fontWeight:700, fontSize:"0.85rem", letterSpacing:"0.1em", cursor:"pointer",
          }}
        >
          Play Again →
        </button>
      </div>
    </div>
  );
}

// ── Lose Screen ──────────────────────────────────────────────

function LoseScreen({ result, onPlayAgain }) {
  const {
    seconds, wrongHits, hintsUsed, totalPages,
    correctHits, pagesVisited, difficulty, funMode,
    giveUp,
  } = result;

  const a = giveUp?.anomaly;
  const wrong = a?.wrongValue || "(unknown)";
  const real  = a?.realValue || "(unknown)";
  const fact  = a?.factLabel || "fact";
  const where = giveUp?.pageTitle ? `on “${giveUp.pageTitle}”` : "on this page";

  return (
    <div style={{
      minHeight:"100vh", background:"#0a0a0f",
      display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", fontFamily:"'Space Mono',monospace",
      color:"#e8e4d8", textAlign:"center", gap:"1.4rem", padding:"2rem 1rem",
    }}>
      <style>{FONT_IMPORT}</style>

      <div style={{
        width:80, height:80, borderRadius:"50%",
        border:"2px solid #f87171",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontFamily:"'Cinzel',serif", fontSize:"2.4rem", fontWeight:900,
        color:"#f87171", boxShadow:"0 0 48px rgba(248,113,113,0.22)",
      }}>
        ✗
      </div>

      <div>
        <h1 style={{
          fontFamily:"'Cinzel',serif", color:"#f87171",
          fontSize:"clamp(1.8rem,5vw,2.8rem)", margin:0, lineHeight:1,
        }}>
          Hunt Failed
        </h1>
        <p style={{
          color:"#6b6880", fontSize:"0.62rem",
          letterSpacing:"0.2em", textTransform:"uppercase", marginTop:"0.4rem",
        }}>
          {difficulty}{funMode ? " · fun mode" : ""} · gave up {where}
        </p>
      </div>

      <div style={{
        maxWidth:520, width:"100%",
        background:"#12121a",
        border:"1px solid rgba(248,113,113,0.25)",
        borderLeft:"4px solid #f87171",
        borderRadius:8,
        padding:"1rem 1.2rem",
        textAlign:"left",
      }}>
        <div style={{
          fontSize:"0.55rem", letterSpacing:"0.2em",
          textTransform:"uppercase", color:"#6b6880", marginBottom:"0.5rem",
        }}>
          Correct answer
        </div>
        <div style={{ fontFamily:"'Cinzel',serif", fontSize:"1.05rem", color:"#e8e4d8" }}>
          The {fact} was wrong:
        </div>
        <div style={{ marginTop:"0.55rem", lineHeight:1.7 }}>
          <span style={{ color:"#f87171", textDecoration:"line-through" }}>{wrong}</span>
          <span style={{ color:"#6b6880" }}> → </span>
          <span style={{ color:"#4ade80" }}>{real}</span>
        </div>
        <div style={{ marginTop:"0.6rem", color:"#6b6880", fontSize:"0.7rem" }}>
          Tip: the anomaly is always the <span style={{ color:"#60a5fa" }}>blue hyperlink</span>.
        </div>
      </div>

      {/* Stats grid (same layout as Win) */}
      <div style={{
        display:"grid", gridTemplateColumns:"repeat(4,1fr)",
        gap:"1px", background:"rgba(255,255,255,0.06)",
        border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
        overflow:"hidden", width:"100%", maxWidth:480,
      }}>
        {[
          ["Pages",   `${correctHits}/${totalPages}`],
          ["Time",    fmt(seconds)],
          ["Wrong",   wrongHits],
          ["Hints",   hintsUsed],
        ].map(([label, val]) => (
          <div key={label} style={{ background:"#12121a", padding:"1rem 0.5rem" }}>
            <div style={{
              fontSize:"0.5rem", letterSpacing:"0.15em",
              textTransform:"uppercase", color:"#6b6880", marginBottom:"0.3rem",
            }}>
              {label}
            </div>
            <div style={{ fontFamily:"'Cinzel',serif", fontSize:"1.35rem", color:"#d4a94a" }}>
              {val}
            </div>
          </div>
        ))}
      </div>

      {/* Articles visited */}
      {pagesVisited && pagesVisited.length > 0 && (
        <div style={{ maxWidth:480, width:"100%" }}>
          <div style={{
            fontSize:"0.52rem", letterSpacing:"0.18em",
            textTransform:"uppercase", color:"#6b6880", marginBottom:"0.55rem",
          }}>
            Articles Visited
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"0.35rem", justifyContent:"center" }}>
            {pagesVisited.map((p, i) => (
              <span key={i} style={{
                fontFamily:"'Space Mono',monospace", fontSize:"0.62rem",
                padding:"0.2rem 0.55rem", borderRadius:4,
                background: "#12121a",
                border: "1px solid rgba(255,255,255,0.06)",
                color: "#6b6880",
              }}>
                {p.title}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onPlayAgain}
        style={{
          background:"#d4a94a", color:"#0a0a0f", border:"none", borderRadius:6,
          padding:"0.85rem 2.5rem", fontFamily:"'Cinzel',serif",
          fontWeight:700, fontSize:"0.85rem", letterSpacing:"0.1em", cursor:"pointer",
        }}
      >
        Try Again →
      </button>
    </div>
  );
}

// ── Root component ───────────────────────────────────────────

export default function App() {
  const [phase,      setPhase]     = useState("start");
  const [gameConfig, setGameConfig]= useState(null);
  const [winResult,  setWinResult] = useState(null);
  const [loseResult, setLoseResult]= useState(null);

  useEffect(() => {
    document.body.style.background = "#0a0a0f";
    document.body.style.margin     = "0";
    document.body.style.minHeight  = "100vh";
  }, []);

  // ── Start game ────────────────────────────────────────────
  function handleStart(config) {
    setGameConfig(config);
    setWinResult(null);
    setLoseResult(null);
    setPhase("game");
  }

  // ── Game complete → results ───────────────────────────────
  function handleWin(result) {
    setWinResult(result);
    setPhase("win");
  }

  function handleLose(result) {
    setLoseResult(result);
    setPhase("lose");
  }

  // ── Reset to start ────────────────────────────────────────
  function handleQuit() {
    setPhase("start");
    setGameConfig(null);
    setWinResult(null);
    setLoseResult(null);
  }

  // Listen for GameScreen "give up" events
  useEffect(() => {
    function onGiveUp(e) {
      const d = e?.detail || {};
      handleLose({
        seconds: d.seconds || 0,
        wrongHits: d.wrongHits || 0,
        hintsUsed: d.hintsUsed || 0,
        totalPages: d.totalPages || 0,
        correctHits: d.correctHits || 0,
        pagesVisited: d.pagesVisited || [],
        difficulty: d.difficulty || "",
        funMode: !!d.funMode,
        giveUp: {
          pageNum: d.pageNum,
          pageTitle: d.pageTitle,
          anomaly: d.anomaly || null,
        },
      });
    }
    window.addEventListener("wikiscav:giveup", onGiveUp);
    return () => window.removeEventListener("wikiscav:giveup", onGiveUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Phase routing ─────────────────────────────────────────

  if (phase === "win" && winResult) {
    return <WinScreen result={winResult} onPlayAgain={handleQuit} />;
  }

  if (phase === "lose" && loseResult) {
    return <LoseScreen result={loseResult} onPlayAgain={handleQuit} />;
  }

  if (phase === "game" && gameConfig) {
    return (
      <GameScreen
        key={`${gameConfig.theme}-${gameConfig.difficulty}-${Date.now()}`}
        difficulty={gameConfig.difficulty}
        settings={gameConfig.settings}
        theme={gameConfig.theme}
        customPage={gameConfig.startPage || gameConfig.customPage}
        customPages={gameConfig.customPages || null}
        onWin={handleWin}
        onQuit={handleQuit}
      />
    );
  }

  return <StartScreen onStart={handleStart} />;
}
